// The server's name at the top of the sidebar opens its menu, like Discord's: settings, notifications, invite, then
// (Chats layout, #141: Apps and Collections, which have no place in its list, and New channel and New category for admins; Classic, #557: Apps and Collections while its list hides them), every server I'm in to switch to, Create or Join a server (there's no server rail, #140), and leave. A dot or a count
// by the name says another server has unread messages or mentions. A small menu under the name on desktop, a bottom
// sheet on phones (the sidebar is then the drawer).
import { useEffect, useRef, useState } from "react";
import type { ListInfo, ServerSummary, ServerUnread } from "@bloom/shared";
import { isNarrow } from "../mobile.ts";
import { unreadLabel } from "../serverUnread.ts";
import { ListGlyph, LockIcon } from "./Lists.tsx";
import { collectionsCollapsed, collectionsLabel, setCollectionsCollapsed, shownCollections } from "./classic/collectionsSection.ts";
import { Sheet } from "./Sheet.tsx";
import { initials } from "./ui.tsx";
import "./server-settings.css";

/** What every layout's menu has. */
export interface ServerMenuBase {
  server: ServerSummary;
  servers: ServerSummary[];
  unread: ServerUnread;
  canInvite: boolean;
  muted?: boolean;
  onSettings: () => void;
  onNotifications: () => void;
  onInvite: () => void;
  onLeave: () => void;
  onSwitch: (id: string) => void;
  onCreate: () => void;
  onJoin: () => void;
}

/** What the Chats layout adds (#141), because its list has no Apps, Collections or categories to put them on. Classic adds Apps and Collections only while Settings → Appearance turns them off in its list (#557). */
export interface ServerMenuExtras {
  /** Collections I can see (custom ones), with the open one marked. */
  collections: ListInfo[];
  openCollection?: string;
  appsOpen: boolean;
  onApps: () => void;
  onCollection: (id: string) => void;
  onNewCollection: () => void;
  /** Owners and admins: make a channel or a category. */
  onNewChannel?: () => void;
  onNewCategory?: () => void;
}

export type ServerMenuProps = ServerMenuBase & Partial<ServerMenuExtras>;

export function ServerMenu({ server, servers, unread, canInvite, muted, collections, openCollection, appsOpen, onApps, onCollection, onNewCollection, onNewChannel, onNewCategory, onSettings, onNotifications, onInvite, onLeave, onSwitch, onCreate, onJoin }: ServerMenuProps) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open || isNarrow()) return;
    const onDown = (e: PointerEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);
  const pick = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  const isAdmin = server.role === "owner" || server.role === "admin";
  // Collections folds like the sidebar's section (the same flag on this device): collapsed, the open one still shows.
  const [listsCollapsed, setListsCollapsed] = useState(() => collectionsCollapsed(server.id));
  const toggleLists = () => {
    setCollectionsCollapsed(server.id, !listsCollapsed);
    setListsCollapsed(!listsCollapsed);
  };
  // Other servers' unread, summed for the dot or count by the name.
  const others = servers.filter((s) => s.id !== server.id).map((s) => unread[s.id]);
  const otherMentions = others.reduce((n, u) => n + (u?.mentions ?? 0), 0);
  const otherUnread = otherMentions > 0 || others.some((u) => u?.unread);
  const otherLabel = otherMentions > 0 ? `${otherMentions} unread mention${otherMentions === 1 ? "" : "s"} in other servers` : otherUnread ? "unread messages in other servers" : "";
  const items = (
    <div className="action-menu" role="menu" aria-label={`${server.name} menu`}>
      {onApps && <button type="button" role="menuitem" className={`action-item${appsOpen ? " current" : ""}`} onClick={pick(onApps)}>Apps</button>}
      {collections && onCollection && onNewCollection && (
      <div className="menu-collections" role="group" aria-label="Collections">
        <button type="button" role="menuitem" className="action-heading menu-collections-toggle" aria-expanded={!listsCollapsed} onClick={toggleLists}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: listsCollapsed ? "rotate(-90deg)" : undefined, transition: "transform .15s" }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
          {collectionsLabel(collections.length, listsCollapsed)}
        </button>
        {shownCollections(collections, listsCollapsed, openCollection).map((l) => (
          <button key={l.id} type="button" role="menuitem" className={`action-item menu-collection${openCollection === l.id ? " current" : ""}`} onClick={pick(() => onCollection(l.id))}>
            <ListGlyph list={l} size={14} />
            <span className="switch-name">{l.name}</span>
            {l.visibility === "private" && <span className="list-lock" title="Only you"><LockIcon size={11} /></span>}
            <span className="list-count">{l.count || ""}</span>
          </button>
        ))}
        <button type="button" role="menuitem" className="action-item" onClick={pick(onNewCollection)}>New collection</button>
      </div>
      )}
      {(onNewChannel || onNewCategory) && (
        <div className="menu-make" role="group" aria-label="Channels">
          {onNewChannel && <button type="button" role="menuitem" className="action-item" onClick={pick(onNewChannel)}>New channel</button>}
          {onNewCategory && <button type="button" role="menuitem" className="action-item" onClick={pick(onNewCategory)}>New category</button>}
        </div>
      )}
      <button type="button" role="menuitem" className="action-item" onClick={pick(onSettings)}>{isAdmin ? "Server settings" : "Server info"}</button>
      <button type="button" role="menuitem" className="action-item" onClick={pick(onNotifications)}>{muted ? "Notification settings (muted)" : "Notification settings"}</button>
      {canInvite && <button type="button" role="menuitem" className="action-item" onClick={pick(onInvite)}>Invite people</button>}
      <div className="switch-servers" role="group" aria-label="Switch server">
        <div className="action-heading" aria-hidden="true">Switch server</div>
        {servers.map((s) => {
          const current = s.id === server.id;
          const u = current ? undefined : unread[s.id];
          const label = unreadLabel(u);
          return (
            <button key={s.id} type="button" role="menuitemradio" aria-checked={current} className={`action-item switch-server${current ? " current" : ""}`} aria-label={label ? `${s.name}, ${label}` : s.name} onClick={pick(() => onSwitch(s.id))}>
              <span className="switch-icon" style={{ background: s.icon ? undefined : s.color }} aria-hidden="true">
                {s.icon ? <img src={s.icon} alt="" width={24} height={24} draggable={false} /> : initials(s.name)}
              </span>
              <span className="switch-name">{s.name}</span>
              {u && u.mentions > 0 ? <span className="mention-pill" aria-hidden="true">{u.mentions > 99 ? "99+" : u.mentions}</span> : u?.unread ? <span className="switch-dot" aria-hidden="true" /> : null}
              {current && (
                <svg className="switch-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
              )}
            </button>
          );
        })}
        <button type="button" role="menuitem" className="action-item" onClick={pick(onCreate)}>Create a server</button>
        <button type="button" role="menuitem" className="action-item" onClick={pick(onJoin)}>Join a server</button>
      </div>
      {server.role !== "owner" && <button type="button" role="menuitem" className="action-item danger" onClick={pick(onLeave)}>Leave server</button>}
    </div>
  );
  return (
    <div className="server-menu-wrap" ref={wrap}>
      <button type="button" className="server-menu-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => {
        // Read the flag on every open: the menu outlives a server switch, and the sidebar may have changed it.
        if (!open) setListsCollapsed(collectionsCollapsed(server.id));
        setOpen((v) => !v);
      }} title={`${server.name}: server menu`} aria-label={otherLabel ? `${server.name}: server menu, ${otherLabel}` : `${server.name}: server menu`}>
        <h2>{server.name}</h2>
        {otherMentions > 0 ? <span className="mention-pill other-servers" aria-hidden="true">{otherMentions > 99 ? "99+" : otherMentions}</span> : otherUnread ? <span className="switch-dot other-servers" aria-hidden="true" /> : null}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open &&
        (isNarrow() ? (
          <Sheet label={`${server.name} menu`} onClose={() => setOpen(false)}>
            {items}
          </Sheet>
        ) : (
          <div className="action-popover">{items}</div>
        ))}
    </div>
  );
}
